<template>
    <div class="bg-white p-4">
        <div class="mb-2 gap-2 flex items-center">
            <span :class="typeBadgeVariants({ color: typeColor })">
                {{ type }}
            </span>
            <span v-if="label" class="text-xs text-iron-500">
                {{ label }}
            </span>
            <!-- Validation Status -->
            <div
                v-if="validation !== undefined"
                class="gap-1 ml-auto flex items-center"
            >
                <CheckCircle2
                    v-if="validation?.valid"
                    :size="16"
                    class="text-green-500"
                />
                <XCircle v-else :size="16" class="text-red-400" />
                <span
                    class="text-xs"
                    :class="
                        validation?.valid ? 'text-green-600' : 'text-red-500'
                    "
                >
                    {{
                        validation?.valid
                            ? (validLabel ??
                              $t(
                                  "page_domains.dns_setup.dns_status.configured",
                              ))
                            : (invalidLabel ??
                              $t("page_domains.dns_setup.dns_status.not_found"))
                    }}
                </span>
            </div>
        </div>
        <div class="gap-y-2 text-sm grid grid-cols-[70px_minmax(0,1fr)]">
            <span class="text-iron-500">Name</span>
            <div class="group gap-2 min-w-0 flex items-center">
                <code :class="codeValueVariants({ size: valueSize })">
                    {{ name }}
                </code>
                <button
                    class="h-7 w-7 rounded-md text-iron-400 hover:bg-iron-100 hover:text-iron-600 flex flex-shrink-0 items-center justify-center transition-colors"
                    @click="$emit('copy', name, `${copyFieldKey}_name`)"
                >
                    <Check
                        v-if="copiedField === `${copyFieldKey}_name`"
                        :size="14"
                        class="text-green-600"
                    />
                    <Copy v-else :size="14" />
                </button>
            </div>
            <span class="text-iron-500">Value</span>
            <div class="group gap-2 min-w-0 flex items-center">
                <code :class="codeValueVariants({ size: valueSize })">
                    {{ value }}
                </code>
                <button
                    class="h-7 w-7 rounded-md text-iron-400 hover:bg-iron-100 hover:text-iron-600 flex flex-shrink-0 items-center justify-center transition-colors"
                    @click="$emit('copy', value, `${copyFieldKey}_value`)"
                >
                    <Check
                        v-if="copiedField === `${copyFieldKey}_value`"
                        :size="14"
                        class="text-green-600"
                    />
                    <Copy v-else :size="14" />
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { cva } from "class-variance-authority";
import { Check, Copy, CheckCircle2, XCircle } from "lucide-vue-next";
import type { DnsValidationRecord } from "@/types/PageDomains";

const props = defineProps<{
    type: string;
    typeColor: "blue" | "purple" | "green" | "amber";
    label?: string;
    name: string;
    value: string;
    copiedField: string | null;
    validation?: DnsValidationRecord | null;
    fieldKey?: string;
    validLabel?: string;
    invalidLabel?: string;
}>();

const copyFieldKey = computed(() => props.fieldKey ?? props.type.toLowerCase());

defineEmits<{
    copy: [text: string, field: string];
}>();

const typeBadgeVariants = cva("rounded px-2 py-0.5 text-xs font-semibold", {
    variants: {
        color: {
            blue: "bg-blue-100 text-blue-700",
            purple: "bg-purple-100 text-purple-700",
            green: "bg-green-100 text-green-700",
            amber: "bg-amber-100 text-amber-700",
        },
    },
    defaultVariants: {
        color: "blue",
    },
});

const codeValueVariants = cva(
    "flex-1 truncate rounded bg-iron-100 px-2 py-1 font-mono text-iron-900",
    {
        variants: {
            size: {
                sm: "text-sm",
                xs: "text-xs",
            },
        },
        defaultVariants: {
            size: "sm",
        },
    },
);

const valueSize = computed(() =>
    props.value.length > 40 || props.name.length > 40 ? "xs" : "sm",
);
</script>
